import React, { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, EyeOff, Eye, Save, Edit3 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Éditeur d'une section d'un livret AITO.
 * - Toggle visible / invisible
 * - Boutons Éditer / Régénérer IA
 * - Contenu markdown affiché en preview (simple) ou en textarea si édition
 *
 * Props :
 *   section          : objet section {key, kind, title, content_md, visible, order, ai_generated_at, origin}
 *   versionId        : id de la version parente
 *   snapshot         : snapshot pour afficher les données déterministes (figures, palmares, ...)
 *   canRegenerate    : bool (IA activée uniquement sur certaines sections)
 *   onSectionUpdated : callback (updated_version)
 */
export const MemoryBookSectionEditor = ({
  section,
  versionId,
  snapshot,
  canRegenerate = false,
  onSectionUpdated,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [visible, setVisible] = useState(section.visible ?? true);
  const [contentDraft, setContentDraft] = useState(section.content_md || "");

  const persistPatch = async (patch) => {
    setSaving(true);
    try {
      const res = await api.patch(
        `/admin/memory-books/versions/${versionId}/sections/${section.key}`,
        patch
      );
      onSectionUpdated?.(res.data);
      toast.success("Section mise à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisible = async (v) => {
    setVisible(v);
    await persistPatch({ visible: v });
  };

  const handleSave = async () => {
    await persistPatch({ content_md: contentDraft });
    setEditing(false);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.post(
        `/admin/memory-books/versions/${versionId}/sections/${section.key}/regenerate`
      );
      setContentDraft(res.data.content_md || "");
      toast.success("Section régénérée depuis le snapshot");
      // refetch parent for consistency
      onSectionUpdated?.({ ...section, content_md: res.data.content_md, ai_generated_at: new Date().toISOString() });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec IA — vérifiez la clé Emergent LLM.");
    } finally {
      setRegenerating(false);
    }
  };

  const originLabel = section.origin === "ai" ? "IA" : section.origin === "human" ? "Manuel" : "Déterministe";
  const originColor = section.origin === "ai"
    ? "bg-[hsl(var(--aito-lagoon))/0.15] text-foreground border-[hsl(var(--aito-lagoon))/0.35]"
    : section.origin === "human"
    ? "bg-[hsl(var(--aito-gold))/0.15] text-foreground border-[hsl(var(--aito-gold))/0.35]"
    : "bg-muted text-muted-foreground";

  const hasDeterministicData = (() => {
    if (!snapshot) return false;
    switch (section.kind) {
      case "figures": return !!(snapshot.key_figures && Object.keys(snapshot.key_figures).length);
      case "palmares": return (snapshot.achievements?.length || 0) > 0;
      case "timeline": return (snapshot.timeline_items?.length || 0) > 0;
      case "competitions": return (snapshot.competitions?.length || 0) > 0;
      case "articles": return (snapshot.articles?.length || 0) > 0;
      case "sources": {
        const src = snapshot.source_ids || {};
        return Object.values(src).some((v) => (v || []).length > 0);
      }
      default: return false;
    }
  })();

  return (
    <AccordionItem
      value={section.key}
      className={`border rounded-md ${visible ? "" : "opacity-70"}`}
      data-testid={`section-editor-${section.key}`}
    >
      <AccordionTrigger className="px-4 hover:no-underline">
        <div className="flex items-center gap-3 flex-1">
          <span className="font-medium">{section.title}</span>
          <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${originColor}`}>
            {originLabel}
          </Badge>
          {!visible && (
            <Badge variant="outline" className="text-[10px] uppercase tracking-wide bg-muted text-muted-foreground">
              Masquée
            </Badge>
          )}
          {section.ai_generated_at && (
            <span className="text-[10px] font-mono text-muted-foreground ml-auto mr-2">
              IA · {new Date(section.ai_generated_at).toLocaleDateString("fr-FR")}
            </span>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 space-y-3">
        {/* Actions row */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Switch
              checked={visible}
              onCheckedChange={handleToggleVisible}
              data-testid={`section-visibility-switch-${section.key}`}
            />
            {visible ? <><Eye className="h-3 w-3" /> Visible dans le PDF</> : <><EyeOff className="h-3 w-3" /> Masquée</>}
          </label>
          {!editing && (section.content_md || section.origin === "human") && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setEditing(true)}
              data-testid={`section-edit-button-${section.key}`}
            >
              <Edit3 className="h-3.5 w-3.5 mr-1" /> Éditer
            </Button>
          )}
          {canRegenerate && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleRegenerate}
              disabled={regenerating}
              data-testid={`section-regenerate-button-${section.key}`}
            >
              {regenerating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
              Régénérer IA
            </Button>
          )}
          {editing && (
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              data-testid={`section-save-button-${section.key}`}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              Enregistrer
            </Button>
          )}
        </div>

        {/* Contenu */}
        {editing ? (
          <Textarea
            value={contentDraft}
            onChange={(e) => setContentDraft(e.target.value)}
            rows={8}
            className="font-serif text-sm leading-relaxed"
            data-testid={`section-textarea-${section.key}`}
          />
        ) : section.content_md ? (
          <div
            className="prose prose-sm max-w-none text-sm leading-relaxed whitespace-pre-wrap"
            data-testid={`section-preview-${section.key}`}
          >
            {section.content_md}
          </div>
        ) : hasDeterministicData ? (
          <p className="text-xs text-muted-foreground italic">
            Cette section utilise directement les données structurées du snapshot. Le rendu final est visible dans le PDF.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            Aucune donnée disponible pour cette section — elle sera automatiquement masquée du PDF.
          </p>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};
